import React, { useState, useRef, useEffect, useCallback, memo } from 'react'
import classNames from 'classnames'
import styles from './styles.module.scss'
import CustomField from '../../../form/CustomField'
import IconBedroom from '/public/icons/icon-bedroom.svg'
import { useResponsive } from '../../../../../providers/ResponsiveContext'
import Bedrooms from '../../../../custom/Bedrooms'
import { useSearchContext } from '../../../../../providers/SearchContext'
import { useRouter } from 'next/router'

// Using memo to prevent unnecessary re-renders
const Bedroom = memo(
  ({
    setFieldValue,
    shadowRef,
    setBedroomActive,
    custom,
    width,
    translations
  }) => {
    const { search, setSearch } = useSearchContext()
    const [show, setShow] = useState(false)
    const [value, setValue] = useState(search?.bedrooms || null)
    const [contentMounted, setContentMounted] = useState(false)
    const wrapperRef = useRef(null)
    const { isMobile } = useResponsive()
    const router = useRouter()

    // Ref to track value updates and prevent loops
    const valueRef = useRef(value)

    // Custom setValue function that updates both local state and form
    const handleSetValue = useCallback(
      (newValue) => {
        // Update local state
        setValue(newValue)

        // Update form field
        setFieldValue('bedrooms', newValue)

        // Update search context

        // Update ref for comparison
        valueRef.current = newValue
      },
      [setFieldValue, setSearch]
    )

    // Memoize the click outside handler to prevent recreating on each render
    const handleClickOutside = useCallback((event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShow(false)
      }
    }, [])

    // Set up and clean up click outside handler
    useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [handleClickOutside])

    // Sync with search context - only update if there's a real change
    useEffect(() => {
      if (search.bedrooms !== valueRef.current) {
        setValue(search.bedrooms)
        valueRef.current = search.bedrooms
      }
    }, [search.bedrooms])

    // Handle shadow overlay
    useEffect(() => {
      if (shadowRef) {
        if (show) {
          shadowRef.current?.classList.add('isVisible')
        } else {
          shadowRef.current?.classList.remove('isVisible')
        }
      }
    }, [shadowRef, show])

    // Update parent component's active state
    useEffect(() => {
      if (setBedroomActive) {
        setBedroomActive(show || value)
      }
    }, [show, value, setBedroomActive])

    // Handle toggle show/hide
    const toggleShow = useCallback(() => {
      setShow((prevShow) => !prevShow)
    }, [])

    // Generate the display text
    const displayText = value
      ? `${translations.bedrooms[router.locale]}: ${value}`
      : translations.bedrooms[router.locale]

    // Use effect to handle mounting/unmounting with proper timing for transitions
    useEffect(() => {
      if (show) {
        // When opening, mount content immediately
        setContentMounted(true)
      } else if (!show && contentMounted) {
        // When closing, wait for transition to finish before unmounting
        const transitionDuration = 300 // Match this with your CSS transition duration
        const timer = setTimeout(() => {
          setContentMounted(false)
        }, transitionDuration)

        return () => clearTimeout(timer)
      }
    }, [show, contentMounted])

    return (
      <div
        ref={wrapperRef}
        className={styles.field}
        role="group"
        aria-labelledby="bedrooms-group"
      >
        <CustomField
          aria-label="Select Bedroom Option"
          type="text"
          readOnly
          angle
          variant={[isMobile && 'full-width']}
          prependIcon={<IconBedroom width={26} height={26} />}
          width={width}
          onClick={toggleShow}
          isActive={value || show}
          value={displayText}
        />

        <div
          className={classNames(
            styles.field__radios,
            show && styles.isShown,
            custom && styles.custom
          )}
        >
          {contentMounted && (
            <Bedrooms value={value} setValue={handleSetValue} />
          )}
        </div>
      </div>
    )
  }
)

// Display name for debugging
Bedroom.displayName = 'Bedroom'

export default Bedroom
